import { useState } from "react";

import { newImageElement } from "@excalidraw/element";

import { useUIAppState } from "../../context/ui-appState";
import { useApp, useExcalidrawSetAppState } from "../App";
import { Dialog } from "../Dialog";
import { ArrowRightIcon } from "../icons";
import { withInternalFallback } from "../hoc/withInternalFallback";
import Spinner from "../Spinner";

import "./AIGenerateDialog.scss";

import type { DataURL } from "../../types";

export type OnAIImageSubmit = (prompt: string) => Promise<{
  imageDataURL?: string | null;
  error?: Error | null;
}>;

export const AIImageDialog = (
  props: { onSubmit: OnAIImageSubmit } | { __fallback: true },
) => {
  const appState = useUIAppState();

  if (appState.openDialog?.name !== "ai-image") {
    return null;
  }

  return <AIImageDialogContent {...props} />;
};

const AIImageDialogContent = withInternalFallback(
  "AIImageDialogContent",
  (props: { onSubmit: OnAIImageSubmit } | { __fallback: true }) => {
    const app = useApp();
    const setAppState = useExcalidrawSetAppState();

    const [prompt, setPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [imageDataURL, setImageDataURL] = useState<string | null>(null);

    const handleGenerate = async () => {
      if ("__fallback" in props || prompt.trim().length < 3 || loading) {
        return;
      }

      setLoading(true);
      setError(null);
      setImageDataURL(null);

      try {
        const result = await props.onSubmit(prompt.trim());
        if (result.error) {
          setError(result.error.message);
        } else if (result.imageDataURL) {
          setImageDataURL(result.imageDataURL);
        } else {
          setError("Nie udało się wygenerować obrazka");
        }
      } catch (err: any) {
        setError(err?.message || "Błąd generowania");
      } finally {
        setLoading(false);
      }
    };

    const handleInsert = async () => {
      if (!imageDataURL) {
        return;
      }

      const fileId = `ai-image-${Date.now()}`;
      const img = new Image();
      img.src = imageDataURL;

      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });

      const maxSize = 400;
      const scale = Math.min(
        1,
        maxSize / Math.max(img.width || maxSize, img.height || maxSize),
      );
      const width = (img.width || maxSize) * scale;
      const height = (img.height || maxSize) * scale;

      const element = newImageElement({
        type: "image",
        x: 100,
        y: 100,
        width,
        height,
        fileId: fileId as any,
        status: "saved",
      });

      app.addFiles([
        {
          id: fileId as any,
          dataURL: imageDataURL as DataURL,
          mimeType: "image/png",
          created: Date.now(),
          lastRetrieved: Date.now(),
        },
      ]);

      app.addElementsFromPasteOrLibrary({
        elements: [element],
        files: null,
        position: "center",
        fitToContent: true,
      });

      setAppState({ openDialog: null });
    };

    const isFallback = "__fallback" in props;

    return (
      <Dialog
        className="ai-generate-dialog"
        onCloseRequest={() => setAppState({ openDialog: null })}
        size={600}
        title="Generuj obrazek AI"
      >
        <div className="ai-generate-dialog__content">
          {isFallback ? (
            <div className="ai-generate-dialog__fallback">
              Generowanie obrazków AI nie jest dostępne w tej wersji.
            </div>
          ) : (
            <>
              <div className="ai-generate-dialog__input-section">
                <label className="ai-generate-dialog__label">
                  Opisz obrazek, który chcesz wygenerować:
                </label>
                <textarea
                  className="ai-generate-dialog__textarea"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="np. Schemat fotosyntezy dla uczniów szkoły podstawowej"
                  rows={3}
                />
                <button
                  className="ai-generate-dialog__button ai-generate-dialog__button--primary"
                  onClick={handleGenerate}
                  disabled={prompt.trim().length < 3 || loading}
                >
                  {loading ? <Spinner /> : <>{ArrowRightIcon} Generuj</>}
                </button>
              </div>

              {error && (
                <div className="ai-generate-dialog__error">{error}</div>
              )}

              {imageDataURL && (
                <div className="ai-generate-dialog__preview">
                  <img
                    src={imageDataURL}
                    alt="Wygenerowany obrazek"
                    className="ai-generate-dialog__preview-image"
                  />
                  <button
                    className="ai-generate-dialog__button ai-generate-dialog__button--success"
                    onClick={handleInsert}
                  >
                    Wstaw na płótno
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Dialog>
    );
  },
);

